// Copyright 2020, J. Donald Tillman.  All rights reserved.
// Waveform Workbench app.

import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Slider } from './slider';
import { Scope } from './scope';
import { updateWave, initAudio, setFreq, setGain, fft } from './engine';
import { CoefficientSlider } from 'coefficient-slider';
import { kron, multiply, dotMultiply, transpose, subset, index, range, lusolve, identity, add } from 'mathjs';
import { Keyboard } from './keyboard';

// convert pitch to frequency, zero is middle C
const pitchFreq = pitch => Math.pow(2, (pitch - 9) / 12) * 440;


// create a Hadamard Walsh Matrix, 2^n square
const hadamard = (n) => {
    // minimal hadamard[2]:
    const h2 = [[1, 1], [1, -1]];
    // generate Hadamard matrix with a recursive Kronecker product
    const hn = n => (n <= 1) ? h2 : kron(h2, hn(n - 1));
    // count the state changes
    const changes = l => l.reduce((a, v, i, s) => a + (s[i - 1] !== v))
    // create the matrix and sort the rows by state changes
    return hn(n).sort((a, b) => changes(a) - changes(b));
}

// Walsh functions 1 through 8, odd (sine) only
const walshFunctions = hadamard(4).filter((r, i) => i % 2);

// extend the size of an array by duplicating each element
const extendo = (a, mult) => {
  const result = new Array(a.length * mult);
  for (let i = 0; i < result.length; i++) {
    result[i] = a[Math.floor(i / mult)];
  }
  return result;
}

// This code here is in a crappy style.  I'm just trying out an experiment.

// return an array of harmonics for the given wave
const harmonics = varray => {
    const re = extendo(varray, 4);
    const im = new Array(re.length).fill(0);
    fft(re, im);
    return im.slice(0, im.length / 2);
}
    
// 8 rows, 32 columns
const walshHarmonics = subset(walshFunctions.map(harmonics),
			      index(range(0, 8), range(1, 9)));


// 8 rows, 8 columns
const reverseHarmonics = identity(8).toArray().map(s => {
    return lusolve(transpose(walshHarmonics), s).flat()});


// generate the table for the article in the console log
reverseHarmonics.forEach((h, i) => {
    const l = h.map(x => `<td>${x.toFixed(3)}</td>`).join('');
    console.log(`<tr><td>${i+1}</td>${l}</tr>`);
});
    


const initialCoefs = [100, 0, 0, 0, 0, 0, 0, 0];
const weights = [...new Array(8).keys()].map(i => 1 / (i + 1));


function App() {
    const [coefs, setCoefs] = useState(initialCoefs);
    const [pitch, setPitch] = useState(0);
    const [volume, setVolume] = useState(50);
    const [audioOn, setAudioOn] = useState(false);
    // const [harmonicMap, setHarmonicMap] = useState(false);
    const [walshSine, setWalshSine] = useState(0);

    const [weighted, setWeighted] = useState(true);
    const scopeRef = useRef();

    useEffect(() => {
	let coefs2 = multiply(.01, coefs);
	if (weighted) {
	    coefs2 = dotMultiply(coefs2, weights);
	}

	const mapMatrix = add(multiply(1 - 0.01 * walshSine, identity(8).toArray()),
			      multiply(0.01 * walshSine, reverseHarmonics));
	const waveform =  multiply([coefs2], mapMatrix, walshFunctions)[0];

	// let waveform;
	// if (harmonicMap) {
	//     waveform = multiply([coefs2], reverseHarmonics, walshFunctions)[0];
	// } else {
	//     waveform = multiply([coefs2], walshFunctions)[0];
	// }
	updateWave(extendo(waveform, 16), scopeRef.current);
    },
	      [coefs, weighted, walshSine]);

    useEffect(() => {
	setFreq(pitchFreq(pitch));
    },
	      [pitch]);

    useEffect(() => {
	setGain(audioOn ? (volume / 100) : 0);
    }, 
	      [volume, audioOn]);

    const handleCoef = (i, val) => {
	setCoefs(cs => {
	    const cs2 = [...cs];
	    cs2[i] = val;
	    return cs2;
	});
    };

    const handleKeyboard = (type, note) => {
	switch (type) {
	case 'start':
            if (!audioOn) {
		initAudio();
		setAudioOn(true);
            }
            // setFreq(pitchFreq(note));
            setPitch(note);
            setGain(volume / 100);
            break;
	case 'note':
            // setFreq(pitchFreq(note));
            setPitch(note);
            break;
	case 'stop':
            setAudioOn(false);
            break;
	default: 
            break;
	}
    };

    return (
	<Frame>
	    <Title>Walsh Waveforms and Spectrums</Title>
	    <Main>
		<Scope waveformSize={256}
		       cycleCount={2}
		       ref={scopeRef} />
		<div>
		    <Button
			on={weighted ? 0 : 1}
			onClick={e => setWeighted(false)}>
			Regular Sliders
		    </Button>
		    <Button
			on={weighted ? 1 : 0}
			onClick={e => setWeighted(true)}>
			Weighted Sliders
		    </Button>
		    <Button
			onClick={e => setCoefs(initialCoefs)}>
			Reset Sliders
		    </Button>
		</div>

		<Sliders>
		    {coefs.map((c, i) => (<CoefficientSlider
					      label={'W' + (i + 1)}
					      readout={'' + c}
					      value={c}
					      range={100}
					      onChange={(v) => handleCoef(i, v)}
					      key={i} />))
		    }
		</Sliders>
		<KDiv>
		    <Keyboard width={700}
			      height={150}
			      low={-24}
			      keyCount={61}
			      playing={pitch}
			      onChange={handleKeyboard} />
		</KDiv>
		<ControlsDiv>
		    <Slider label="Walsh to Sine Map:"
			    value={walshSine}
			    range={100}
			    onChange={setWalshSine}
			    readout={walshSine} />
		    


		    <Slider label="Volume:"
			    value={volume}
			    range={100}
			    onChange={setVolume}
			    readout={volume} />
		</ControlsDiv>
	    </Main>
	</Frame>
    );
}

const Frame = styled.div`
    width: 800px;
    margin: 0;
    border: solid 1px gray;
    font-family: Helvetica, sans-serif;
    font-size: 90%;
    display: flex;
    flex-direction: column;
    border: solid 1px gray;
`;

const Title = styled.div`
    font-size: 120%;
    text-align: center;
    border-bottom: solid 1px gray;
    padding: 10px 0 12px 0;`;

const Main = styled.div`
    padding: 0 18px;
    margin: 18px;
`;

const Sliders = styled.div`
    display: flex;
    height: 270px;
    display: flex;
    justify-content: space-around;
`;

const KDiv = styled.div`
    clear: both;
    display: flex;
    justify-content: center;
    height: 150px;
`;


const ControlsDiv = styled.div`
`;


const Button = styled.button`
    color: ${props => props.on ? 'white' : 'black'};
    background-color: ${props => props.on ? 'green' : 'white'};
    border: solid 1px purple;
    border-radius: 5px;
    margin: 5px;
`;



export default App;
